import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Bell, Monitor, Save, CreditCard, KeyRound, ShieldCheck, Building2, FileCheck2, AlertTriangle } from 'lucide-react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { changePasswordWithReauthentication, getPasswordChangeErrorMessage, validatePasswordChange } from '../services/passwordChange';
import { validateCompanyChangeForm } from '../services/companyIdentity';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Redimensiona y recorta cuadrado a `size`px → data URL JPEG (sin Firebase Storage).
function resizeImage(file, size = 256) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const min = Math.min(img.width, img.height);
            const sx = (img.width - min) / 2, sy = (img.height - min) / 2;
            const canvas = document.createElement('canvas');
            canvas.width = size; canvas.height = size;
            canvas.getContext('2d').drawImage(img, sx, sy, min, min, 0, 0, size, size);
            resolve(canvas.toDataURL('image/jpeg', 0.82));
        };
        img.onerror = reject;
        img.src = url;
    });
}

const Settings = () => {
    const { user, userProfile, getAuthHeaders, refreshUserProfile } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordFeedback, setPasswordFeedback] = useState(null);
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companyFeedback, setCompanyFeedback] = useState(null);
    const [companyFile, setCompanyFile] = useState(null);
    const [companyForm, setCompanyForm] = useState({
        ruc: userProfile?.ruc || '',
        companyName: userProfile?.companyName || '',
        sameCompanyConfirmed: false,
        syncOpenExpedientes: false,
    });

    const [profile, setProfile] = useState({
        name: userProfile?.displayName || user?.displayName || 'Usuario',
        email: user?.email || userProfile?.email || '',
        phone: userProfile?.phone || '',
        photoURL: userProfile?.photoURL || user?.photoURL || ''
    });

    // Mock State for Alerts
    const [alerts, setAlerts] = useState({
        emailNotifications: true,
        dailyDigest: false,
        criticalAlerts: true
    });

    // Mock State for System
    const [system, setSystem] = useState({
        darkMode: false,
        defaultFilter: 'Todos'
    });

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) { alert('Imagen muy grande (máx 8MB).'); return; }

        try {
            setLoading(true);
            const dataUrl = await resizeImage(file, 256); // redimensiona en el navegador
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/users/me`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ photoURL: dataUrl }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Error al guardar');
            setProfile(prev => ({ ...prev, photoURL: dataUrl }));
            alert('Foto de perfil actualizada.');
        } catch (error) {
            alert(`Error al subir la imagen: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/users/me`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ displayName: profile.name, phone: profile.phone }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error || 'Error al guardar');
            alert('Configuración guardada correctamente.');
        } catch (error) {
            alert('Error al guardar la configuración.');
        } finally {
            setLoading(false);
        }
    };


    const handleCompanyChange = async (event) => {
        event.preventDefault();
        const validationError = validateCompanyChangeForm({
            ...companyForm,
            file: companyFile,
        });
        if (validationError) {
            setCompanyFeedback({ type: 'error', message: validationError });
            return;
        }

        setCompanyLoading(true);
        setCompanyFeedback(null);
        try {
            const body = new FormData();
            body.append('ruc', companyForm.ruc.trim());
            body.append('companyName', companyForm.companyName.trim());
            body.append('confirmSameCompany', String(companyForm.sameCompanyConfirmed));
            body.append('syncOpenExpedientes', String(companyForm.syncOpenExpedientes));
            body.append('rnp', companyFile);

            const headers = await getAuthHeaders();
            const response = await fetch(API_URL + '/api/users/me/company', {
                method: 'PATCH',
                headers,
                body,
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                if (data.detectedCompanyName) {
                    setCompanyForm((previous) => ({ ...previous, companyName: data.detectedCompanyName }));
                }
                throw new Error(data.error || 'No se pudieron actualizar los datos de empresa.');
            }

            setCompanyForm({
                ruc: data.user.ruc,
                companyName: data.user.companyName,
                sameCompanyConfirmed: false,
                syncOpenExpedientes: false,
            });
            setCompanyFile(null);
            const input = document.getElementById('company-rnp-file');
            if (input) input.value = '';
            await refreshUserProfile();
            const updatedCount = data.updatedExpedientes || 0;
            const message = companyForm.syncOpenExpedientes
                ? 'Datos actualizados. Se sincronizaron ' + updatedCount + ' expedientes en preparación. Revisa y vuelve a generar los anexos PDF existentes antes de presentarlos.'
                : 'Datos actualizados. Tus expedientes existentes conservaron sus datos; si corresponde, corrígelos en cada expediente.';
            setCompanyFeedback({ type: 'success', message });
        } catch (error) {
            setCompanyFeedback({ type: 'error', message: error.message });
        } finally {
            setCompanyLoading(false);
        }
    };
    const handlePasswordChange = async (event) => {
        event.preventDefault();
        const validationError = validatePasswordChange(passwordForm);
        if (validationError) {
            setPasswordFeedback({ type: 'error', message: validationError });
            return;
        }

        setPasswordLoading(true);
        setPasswordFeedback(null);
        try {
            await changePasswordWithReauthentication({
                user,
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
                createCredential: (email, password) => EmailAuthProvider.credential(email, password),
                reauthenticate: reauthenticateWithCredential,
                updatePassword,
            });
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setPasswordFeedback({ type: 'success', message: 'Contraseña actualizada. Usa la nueva contraseña la próxima vez que inicies sesión.' });
        } catch (error) {
            setPasswordFeedback({ type: 'error', message: getPasswordChangeErrorMessage(error) });
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Configuración</h1>
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>Administra tu perfil, preferencias y notificaciones.</p>
            </div>

            <div style={{ display: 'flex', gap: '2rem', flexDirection: 'column', md: 'row' }}>
                {/* Sidebar Navigation for Settings */}
                <div className="card" style={{ width: '100%', maxWidth: '250px', height: 'fit-content', padding: '0.5rem' }}>
                    <button
                        onClick={() => setActiveTab('profile')}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem 1rem',
                            borderRadius: 'var(--radius)',
                            backgroundColor: activeTab === 'profile' ? 'hsl(var(--primary))' : 'transparent',
                            color: activeTab === 'profile' ? '#fff' : 'hsl(var(--muted-foreground))',
                            fontWeight: activeTab === 'profile' ? '600' : '400',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left'
                        }}
                    >
                        <User size={18} /> Perfil
                    </button>
                    <button
                        onClick={() => setActiveTab('alerts')}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem 1rem',
                            borderRadius: 'var(--radius)',
                            backgroundColor: activeTab === 'alerts' ? 'hsl(var(--primary))' : 'transparent',
                            color: activeTab === 'alerts' ? '#fff' : 'hsl(var(--muted-foreground))',
                            fontWeight: activeTab === 'alerts' ? '600' : '400',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left'
                        }}
                    >
                        <Bell size={18} /> Notificaciones
                    </button>
                    <button
                        onClick={() => setActiveTab('system')}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem 1rem',
                            borderRadius: 'var(--radius)',
                            backgroundColor: activeTab === 'system' ? 'hsl(var(--primary))' : 'transparent',
                            color: activeTab === 'system' ? '#fff' : 'hsl(var(--muted-foreground))',
                            fontWeight: activeTab === 'system' ? '600' : '400',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left'
                        }}
                    >
                        <Monitor size={18} /> Sistema
                    </button>
                                        <button
                        onClick={() => setActiveTab('company')}
                        style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                            padding: '0.75rem 1rem', borderRadius: 'var(--radius)',
                            backgroundColor: activeTab === 'company' ? 'hsl(var(--primary))' : 'transparent',
                            color: activeTab === 'company' ? '#fff' : 'hsl(var(--muted-foreground))',
                            fontWeight: activeTab === 'company' ? '600' : '400',
                            border: 'none', cursor: 'pointer', textAlign: 'left'
                        }}
                    >
                        <Building2 size={18} /> Empresa
                    </button>
<button
                        onClick={() => setActiveTab('security')}
                        style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem',
                            padding: '0.75rem 1rem', borderRadius: 'var(--radius)',
                            backgroundColor: activeTab === 'security' ? 'hsl(var(--primary))' : 'transparent',
                            color: activeTab === 'security' ? '#fff' : 'hsl(var(--muted-foreground))',
                            fontWeight: activeTab === 'security' ? '600' : '400',
                            border: 'none', cursor: 'pointer', textAlign: 'left'
                        }}
                    >
                        <KeyRound size={18} /> Seguridad
                    </button>
                </div>

                {/* Content Area */}
                <div style={{ flex: 1 }}>
                    {activeTab === 'profile' && (
                        <div className="card" style={{ padding: '2rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <User size={20} /> Información Personal
                            </h2>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem' }}>
                                <div style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', backgroundColor: 'hsl(var(--muted))', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '2px solid hsl(var(--border))' }}>
                                    {profile.photoURL ? (
                                        <img src={profile.photoURL} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <User size={40} color="hsl(var(--muted-foreground))" />
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="avatar-upload" className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-block', padding: '0.5rem 1rem', fontSize: '0.875rem' }}>
                                        Cambiar Foto
                                    </label>
                                    <input
                                        id="avatar-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>JPG, PNG o GIF. Máx 2MB.</p>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Nombre Completo</label>
                                    <input
                                        type="text"
                                        value={profile.name}
                                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Correo Electrónico</label>
                                    <input
                                        type="email"
                                        value={profile.email}
                                        disabled
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', backgroundColor: 'hsl(var(--muted))', opacity: 0.7 }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Teléfono</label>
                                    <input
                                        type="tel"
                                        value={profile.phone}
                                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    />
                                </div>

                                <div style={{ borderTop: '1px solid hsl(var(--border))', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <CreditCard size={18} /> Plan Actual
                                    </h3>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: 'var(--radius)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                        <div>
                                            <span style={{ fontWeight: 'bold', color: '#059669' }}>Plan Premium</span>
                                            <p style={{ fontSize: '0.875rem', color: '#059669', opacity: 0.8 }}>Acceso ilimitado a todas las funciones.</p>
                                        </div>
                                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#059669' }}>Activo</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'company' && (
                        <div className="card" style={{ padding: '2rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Building2 size={20} /> Datos de empresa
                            </h2>
                            <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '1.25rem', lineHeight: 1.6 }}>
                                Actualiza el RUC y la razón social de esta cuenta con una constancia RNP legible.
                            </p>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                <div style={{ padding: '1rem', borderRadius: 'var(--radius)', background: 'hsl(var(--muted))' }}>
                                    <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', marginBottom: '0.3rem' }}>RUC actual</div>
                                    <strong>{userProfile?.ruc || 'Sin registrar'}</strong>
                                </div>
                                <div style={{ padding: '1rem', borderRadius: 'var(--radius)', background: 'hsl(var(--muted))' }}>
                                    <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', marginBottom: '0.3rem' }}>Razón social actual</div>
                                    <strong>{userProfile?.companyName || 'Sin registrar'}</strong>
                                </div>
                            </div>

                            <div style={{ padding: '1rem', border: '1px solid rgba(245, 158, 11, 0.35)', background: 'rgba(245, 158, 11, 0.08)', borderRadius: 'var(--radius)', marginBottom: '1.5rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                                <AlertTriangle size={19} style={{ color: '#b45309', flexShrink: 0, marginTop: 2 }} />
                                <div style={{ color: 'hsl(var(--foreground))', fontSize: '0.875rem', lineHeight: 1.55 }}>
                                    <strong>Solo para corregir la misma empresa.</strong> Si el RUC pertenece a otra persona jurídica, registra una cuenta independiente en el Panel Admin para mantener separados sus expedientes. Los PDF ya generados no se reemplazan automáticamente.
                                </div>
                            </div>

                            {companyFeedback && (
                                <div role="status" aria-live="polite" style={{
                                    marginBottom: '1rem', padding: '0.85rem 1rem', borderRadius: 'var(--radius)',
                                    color: companyFeedback.type === 'success' ? '#047857' : '#b91c1c',
                                    background: companyFeedback.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                                    border: '1px solid ' + (companyFeedback.type === 'success' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'),
                                }}>
                                    {companyFeedback.message}
                                </div>
                            )}

                            <form onSubmit={handleCompanyChange} style={{ display: 'grid', gap: '1rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                                    <label style={{ display: 'grid', gap: '0.45rem', fontWeight: 500 }}>
                                        RUC nuevo
                                        <input
                                            inputMode="numeric"
                                            autoComplete="off"
                                            value={companyForm.ruc}
                                            onChange={(event) => setCompanyForm({ ...companyForm, ruc: event.target.value.replace(/\D/g, '').slice(0, 11) })}
                                            placeholder="11 dígitos"
                                            maxLength={11}
                                            required
                                            style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: '0.45rem', fontWeight: 500 }}>
                                        Razón social según RNP
                                        <input
                                            value={companyForm.companyName}
                                            onChange={(event) => setCompanyForm({ ...companyForm, companyName: event.target.value })}
                                            placeholder="Tal como aparece en la constancia"
                                            maxLength={200}
                                            required
                                            style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                        />
                                    </label>
                                </div>

                                <label style={{ display: 'grid', gap: '0.45rem', fontWeight: 500 }}>
                                    Constancia RNP vigente en PDF
                                    <input
                                        id="company-rnp-file"
                                        type="file"
                                        accept="application/pdf,.pdf"
                                        onChange={(event) => setCompanyFile(event.target.files?.[0] || null)}
                                        required
                                        style={{ width: '100%', padding: '0.65rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    />
                                    <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', fontWeight: 400 }}>
                                        Máximo 15 MB. El sistema leerá el documento y exige coincidencia exacta de RUC y razón social; no consulta directamente el estado en OSCE.
                                    </span>
                                </label>

                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', lineHeight: 1.5, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={companyForm.sameCompanyConfirmed}
                                        onChange={(event) => setCompanyForm({ ...companyForm, sameCompanyConfirmed: event.target.checked })}
                                        style={{ marginTop: 4 }}
                                    />
                                    <span>Confirmo que la constancia corresponde a la misma empresa de esta cuenta y que el cambio corrige un dato previamente registrado.</span>
                                </label>

                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', lineHeight: 1.5, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={companyForm.syncOpenExpedientes}
                                        onChange={(event) => setCompanyForm({ ...companyForm, syncOpenExpedientes: event.target.checked })}
                                        style={{ marginTop: 4 }}
                                    />
                                    <span>
                                        Sincronizar RUC y razón social en expedientes no presentados
                                        <small style={{ display: 'block', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                                            Actualiza los datos del postor en expedientes en preparación. Los anexos PDF existentes conservan sus datos y deben revisarse o regenerarse.
                                        </small>
                                    </span>
                                </label>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid hsl(var(--border))', paddingTop: '1rem', marginTop: '0.25rem' }}>
                                    <button type="submit" disabled={companyLoading} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', opacity: companyLoading ? 0.7 : 1 }}>
                                        <FileCheck2 size={17} /> {companyLoading ? 'Revisando constancia…' : 'Validar y guardar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                    {activeTab === 'alerts' && (
                        <div className="card" style={{ padding: '2rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Bell size={20} /> Preferencias de Notificación
                            </h2>

                            <div style={{ display: 'grid', gap: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)' }}>
                                    <div>
                                        <h3 style={{ fontWeight: '500' }}>Notificaciones por Correo</h3>
                                        <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Recibe alertas cuando se encuentren nuevas oportunidades.</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={alerts.emailNotifications}
                                        onChange={(e) => setAlerts({ ...alerts, emailNotifications: e.target.checked })}
                                        style={{ width: '1.25rem', height: '1.25rem' }}
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)' }}>
                                    <div>
                                        <h3 style={{ fontWeight: '500' }}>Resumen Diario</h3>
                                        <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Recibe un solo correo al día con todas las novedades.</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={alerts.dailyDigest}
                                        onChange={(e) => setAlerts({ ...alerts, dailyDigest: e.target.checked })}
                                        style={{ width: '1.25rem', height: '1.25rem' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'system' && (
                        <div className="card" style={{ padding: '2rem' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Monitor size={20} /> Preferencias del Sistema
                            </h2>

                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Tema</label>
                                    <select
                                        value={system.darkMode ? 'dark' : 'light'}
                                        onChange={(e) => setSystem({ ...system, darkMode: e.target.value === 'dark' })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    >
                                        <option value="light">Claro</option>
                                        <option value="dark">Oscuro</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Filtro por Defecto</label>
                                    <select
                                        value={system.defaultFilter}
                                        onChange={(e) => setSystem({ ...system, defaultFilter: e.target.value })}
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    >
                                        <option value="Todos">Todos</option>
                                        <option value="Servicios">Servicios</option>
                                        <option value="Bienes">Bienes</option>
                                        <option value="Obras">Obras</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div className="card" style={{ padding: '2rem', maxWidth: '680px' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <ShieldCheck size={20} /> Seguridad de la cuenta
                            </h2>
                            <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '1.75rem', lineHeight: 1.6 }}>
                                Confirma tu contraseña actual para establecer una nueva. Necesitas al menos 8 caracteres.
                            </p>

                            <form onSubmit={handlePasswordChange} style={{ display: 'grid', gap: '1.25rem' }}>
                                <div>
                                    <label htmlFor="current-password" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Contraseña actual</label>
                                    <input
                                        id="current-password"
                                        name="currentPassword"
                                        type="password"
                                        autoComplete="current-password"
                                        value={passwordForm.currentPassword}
                                        onChange={(event) => { setPasswordForm({ ...passwordForm, currentPassword: event.target.value }); setPasswordFeedback(null); }}
                                        required
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="new-password" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Nueva contraseña</label>
                                    <input
                                        id="new-password"
                                        name="newPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        value={passwordForm.newPassword}
                                        onChange={(event) => { setPasswordForm({ ...passwordForm, newPassword: event.target.value }); setPasswordFeedback(null); }}
                                        minLength={8}
                                        aria-describedby="password-requirements"
                                        required
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    />
                                    <p id="password-requirements" style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem', marginTop: '0.45rem' }}>Usa 8 caracteres o más.</p>
                                </div>
                                <div>
                                    <label htmlFor="confirm-password" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Confirmar nueva contraseña</label>
                                    <input
                                        id="confirm-password"
                                        name="confirmPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        value={passwordForm.confirmPassword}
                                        onChange={(event) => { setPasswordForm({ ...passwordForm, confirmPassword: event.target.value }); setPasswordFeedback(null); }}
                                        minLength={8}
                                        required
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}
                                    />
                                </div>

                                {passwordFeedback && (
                                    <div
                                        role={passwordFeedback.type === 'error' ? 'alert' : 'status'}
                                        aria-live="polite"
                                        style={{
                                            padding: '0.85rem 1rem', borderRadius: 'var(--radius)', lineHeight: 1.5,
                                            color: passwordFeedback.type === 'error' ? '#b91c1c' : '#047857',
                                            backgroundColor: passwordFeedback.type === 'error' ? '#fef2f2' : '#ecfdf5',
                                            border: `1px solid ${passwordFeedback.type === 'error' ? '#fecaca' : '#a7f3d0'}`
                                        }}
                                    >
                                        {passwordFeedback.message}
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid hsl(var(--border))', paddingTop: '1.25rem', marginTop: '0.25rem' }}>
                                    <button type="submit" disabled={passwordLoading} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', opacity: passwordLoading ? 0.7 : 1 }}>
                                        <KeyRound size={17} /> {passwordLoading ? 'Actualizando...' : 'Cambiar contraseña'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                    {!['security', 'company'].includes(activeTab) && (
                    <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="btn btn-primary"
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem' }}
                        >
                            {loading ? 'Guardando...' : <><Save size={18} /> Guardar Cambios</>}
                        </button>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;


